import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { useShaderModelOptions } from './useShaderModelOptions'
import type { ShaderProviderConfig, ShaderProviderSecrets } from '@/lib/canvas/shader/authoring/types'

interface ShaderModelPickerProps {
  config: ShaderProviderConfig
  secrets: ShaderProviderSecrets
  onChange: (model: string) => void
}

/** The model list, filled live from the provider, with a free-text id for anything not listed. */
export function ShaderModelPicker({ config, secrets, onChange }: ShaderModelPickerProps) {
  const { options, loading, error } = useShaderModelOptions(config, secrets)
  const [custom, setCustom] = useState(config.model)
  useEffect(() => setCustom(config.model), [config.model])

  // The current model stays selectable even when the live list doesn't include it.
  const items = useMemo(
    () => (config.model && !options.some((o) => o.id === config.model) ? [{ id: config.model, label: config.model }, ...options] : options),
    [options, config.model],
  )

  const handleCustomChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setCustom(e.target.value), [])
  const commitCustom = useCallback(() => {
    const model = custom.trim()
    if (model && model !== config.model) onChange(model)
  }, [custom, config.model, onChange])
  const handleCustomKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter') commitCustom()
  }, [commitCustom])

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs">Model</span>
      <Select value={config.model || undefined} onValueChange={onChange}>
        <SelectTrigger className="h-7 text-xs" aria-label="Model">
          <SelectValue placeholder={loading ? 'Loading models…' : 'Pick a model'} />
        </SelectTrigger>
        <SelectContent>
          {items.map((option) => (
            <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        inputSize="sm"
        aria-label="Model id"
        placeholder="Or type a model id"
        spellCheck={false}
        value={custom}
        onChange={handleCustomChange}
        onBlur={commitCustom}
        onKeyDown={handleCustomKey}
      />
      {error && <p className="text-[10px] text-muted-foreground">Could not list models ({error}). Type an id instead.</p>}
    </div>
  )
}
